import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Lock, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { parseUpgradeRequiredKey } from "@/lib/queryClient";
import { isEntitlementKey, type EntitlementKey } from "@shared/entitlements";
import {
  OPERATOR_ADDONS,
  PM_ADDONS,
  PLAN_TIER_PER_ROOM_CENTS,
  isModuleKey,
  moduleLabel,
  modulePerRoomCents,
} from "@shared/modular-pricing";
import { useEntitlements } from "@/hooks/use-entitlements";
import { useIsV3Billing } from "@/hooks/use-v3-billing";

function moduleUpgradeCopy(key: EntitlementKey): { label: string; priceLine: string } {
  if (isModuleKey(key)) {
    return {
      label: moduleLabel(key),
      priceLine: `Add it for $${modulePerRoomCents(key) / 100}/room/mo — or go Pro at $${PLAN_TIER_PER_ROOM_CENTS.pro / 100}/room for every module.`,
    };
  }
  if (key === "revenue") {
    return {
      label: OPERATOR_ADDONS.revenue.label,
      priceLine: `Add it for $${OPERATOR_ADDONS.revenue.flatCents / 100}/mo + $${OPERATOR_ADDONS.revenue.perRoomCents / 100}/room.`,
    };
  }
  if (key === "research") {
    return {
      label: OPERATOR_ADDONS.research.label,
      priceLine: `Add it for $${OPERATOR_ADDONS.research.flatCents / 100}/mo.`,
    };
  }
  if (key === "owner_portals") {
    return {
      label: PM_ADDONS.owner_portals.label,
      priceLine: `$${PM_ADDONS.owner_portals.perOwnerCents / 100}/owner/mo — owners with a Research, Revenue, or Analytics package are free.`,
    };
  }
  if (key === "cpm_listing") {
    return {
      label: PM_ADDONS.cpm_listing.label,
      priceLine: `$${PM_ADDONS.cpm_listing.flatCents / 100}/mo.`,
    };
  }
  return { label: "This feature", priceLine: "Available as an add-on to your plan." };
}

/**
 * In-context upgrade prompt for a modular entitlement gate — the graceful
 * face of a 403 UPGRADE_REQUIRED carrying a module key: names the module,
 * shows its price, and deep-links into the plan editor with the module
 * highlighted. Never a blank page or raw 403.
 */
export function ModuleUpgradePanel({
  moduleKey,
  className,
}: {
  moduleKey: EntitlementKey;
  className?: string;
}) {
  const { label, priceLine } = moduleUpgradeCopy(moduleKey);
  // v3 per-property accounts have every module — never pitch an add-on.
  const isV3 = useIsV3Billing();
  if (isV3) return null;
  return (
    <div
      className={cn("flex items-start justify-center p-4 sm:p-8", className)}
      data-testid="panel-module-upgrade"
    >
      <div className="mt-6 w-full sm:max-w-md rounded-lg border bg-background p-6 shadow-sm text-center space-y-3">
        <div className="mx-auto w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
          <Lock size={18} className="text-brand" />
        </div>
        <h3 className="text-lg font-bold" data-testid="text-module-upgrade-label">
          {label} isn't on your plan yet
        </h3>
        <p className="text-sm text-muted-foreground" data-testid="text-module-upgrade-price">
          {priceLine}
        </p>
        <Link href={`/app/account/plan?highlight=${moduleKey}`}>
          <Button className="bg-brand hover:bg-brand/90 text-white font-semibold" data-testid="button-module-upgrade">
            <Sparkles size={14} className="mr-1.5" /> Add to my plan
          </Button>
        </Link>
      </div>
    </div>
  );
}

/**
 * Extract the modular entitlement key from a 403 UPGRADE_REQUIRED error, or
 * null when the error is a legacy tier-lock (whose keys are menu-lock ids
 * like "members.members" — those keep the legacy UpgradeContent path).
 */
export function parseModuleUpgradeKey(error: unknown): EntitlementKey | null {
  const key = parseUpgradeRequiredKey(error);
  return key && isEntitlementKey(key) ? key : null;
}

/**
 * Declarative gate for sections that shouldn't even fire their query when the
 * module is missing. Renders children while entitlements load (the server
 * still hard-enforces) and the upgrade panel once a missing module is known.
 */
export function ModuleGate({
  module,
  children,
}: {
  module: EntitlementKey;
  children: React.ReactNode;
}) {
  const entitlements = useEntitlements();
  if (!entitlements.has(module)) {
    return <ModuleUpgradePanel moduleKey={module} />;
  }
  return <>{children}</>;
}
